import { assetUrl } from "@/i18n/assets";
import { Link } from "@/i18n/Link";
import { useCopy } from "@/i18n/LocaleContext";

interface Silhouette {
  name: string;
  to: string;
  label: string;
  src: string;
  webp?: string;
  w: number;
  h: number;
}

const COPY = {
  en: {
    ariaLabel: "Browse parts by trailer type",
    items: [
      { name: "Utility Trailer", label: "Utility trailer - view tires & wheels" },
      { name: "Enclosed Cargo", label: "Enclosed cargo trailer - view hubs, drums & brakes" },
      { name: "Boat Trailer", label: "Boat trailer - view jacks & couplers" },
      { name: "Snowmobile Trailer", label: "Snowmobile trailer - view suspension parts" },
    ],
  },
  es: {
    ariaLabel: "Explorar partes por tipo de remolque",
    items: [
      { name: "Remolque utilitario", label: "Remolque utilitario - ver llantas y ruedas" },
      { name: "Carga cerrada", label: "Remolque de carga cerrada - ver mazas, tambores y frenos" },
      { name: "Remolque para botes", label: "Remolque para botes - ver gatos y acopladores" },
      { name: "Remolque para motos de nieve", label: "Remolque para motos de nieve - ver partes de suspension" },
    ],
  },
};

const SILHOUETTE_DATA: Omit<Silhouette, "name" | "label">[] = [
  { to: "/products/tires-wheels/", src: "/silhouettes/utility.png", w: 948, h: 284 },
  { to: "/products/hubs/", src: "/silhouettes/enclosed.png", webp: "/silhouettes/enclosed.webp", w: 2819, h: 1379 },
  { to: "/products/jacks-couplers/", src: "/silhouettes/boat.png", webp: "/silhouettes/boat.webp", w: 1435, h: 461 },
  { to: "/products/suspension-parts/", src: "/silhouettes/snowmobile.png", webp: "/silhouettes/snowmobile.webp", w: 2147, h: 982 },
];

export function TrailerSilhouettes() {
  const t = useCopy(COPY);

  const row = (key: string) => (
    <div key={key} className="flex items-center gap-12 md:gap-20 px-6 md:px-10 shrink-0">
      {SILHOUETTE_DATA.map((s, i) => (
        <Link
          key={`${i}-${key}`}
          to={s.to}
          aria-label={t.items[i].label}
          className="group flex flex-col items-center shrink-0 transition-transform hover:scale-[1.04] focus-visible:scale-[1.04]"
        >
          <div className="h-20 md:h-28 flex items-center">
            <picture className="block h-full">
              {s.webp && <source srcSet={assetUrl(s.webp)} type="image/webp" />}
              <img
                src={assetUrl(s.src)}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                width={s.w}
                height={s.h}
                className="h-20 md:h-28 w-auto opacity-95 group-hover:opacity-100 transition-opacity select-none pointer-events-none"
                style={{ aspectRatio: `${s.w} / ${s.h}` }}
                draggable={false}
              />
            </picture>
          </div>
          <div className="mt-2 text-center text-[11px] md:text-xs uppercase tracking-[0.18em] text-white/80 group-hover:text-white transition-colors">
            {t.items[i].name}
          </div>
        </Link>
      ))}
    </div>
  );
  return (
    <section
      aria-label={t.ariaLabel}
      className="relative overflow-hidden bg-primary marquee-pause"
      style={{
        backgroundImage:
          "linear-gradient(180deg, hsl(9 100% 47%) 0%, hsl(9 100% 40%) 100%)",
      }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" aria-hidden="true">
        <svg width="100%" height="100%">
          <pattern id="diag" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="20" stroke="white" strokeWidth="2" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#diag)" />
        </svg>
      </div>
      <div className="relative py-8 md:py-10">
        <div className="flex w-max animate-marquee will-change-transform">
          {row("a")}
          {row("b")}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-primary to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-primary to-transparent" />
      </div>
    </section>
  );
}
